import * as THREE from 'three';

export type Tool = 'c4' | 'plane' | 'airstrike';
export type Action =
  'use' | 'useStart' | 'useEnd' | 'detonate' | 'rebuild' | 'instant' | 'pause' | 'overview' | 'debug';

interface Finger {
  x: number;
  y: number;
  startX: number;
  startY: number;
  button: number;
  touch: boolean;
  moved: boolean;
  pan: boolean;
}

/** Canvas gestures and keyboard shortcuts share the same actions as the dock. */
export class Input {
  cancelListeners = new Set<(reason?: 'interface') => void>();
  enabled = true;
  keys = new Set<string>();
  aim = new THREE.Vector2();
  orbit = new THREE.Vector2();
  pan = new THREE.Vector2();
  zoom = 0;
  pointers = new Map<number, Finger>();
  touch = false;
  touchPan = true;
  gesture = false;
  onAction: (action: Action) => void = () => {};
  onTool: (tool: Tool) => void = () => {};
  onGesture: () => void = () => {};

  constructor(readonly canvas: HTMLCanvasElement) {
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    canvas.addEventListener('pointerdown', (event) => this.down(event));
    canvas.addEventListener('pointermove', (event) => this.move(event));
    canvas.addEventListener('pointerup', (event) => this.up(event));
    canvas.addEventListener('pointercancel', () => this.cancel());
    canvas.addEventListener('lostpointercapture', (event) => {
      if (this.pointers.has(event.pointerId)) this.cancel();
    });
    canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        if (!this.enabled) return;
        this.setAim(event.clientX, event.clientY);
        const units = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
        this.zoom += THREE.MathUtils.clamp(event.deltaY * units * 0.001, -0.7, 0.7);
      },
      { passive: false },
    );
    window.addEventListener('keydown', (event) => this.key(event, true));
    window.addEventListener('keyup', (event) => this.key(event, false));
    window.addEventListener('blur', () => this.cancel());
    window.addEventListener('resize', () => this.cancel());
    window.addEventListener('orientationchange', () => this.cancel());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.cancel();
    });
  }

  private down(event: PointerEvent): void {
    if (!this.enabled || event.button > 2) return;
    event.preventDefault();
    this.canvas.focus({ preventScroll: true });
    this.onGesture();
    const touch = event.pointerType === 'touch';
    this.touch ||= touch;
    this.canvas.setPointerCapture(event.pointerId);
    const pan = event.button === 1 || event.shiftKey;
    this.pointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      startX: event.clientX,
      startY: event.clientY,
      button: event.button,
      touch,
      moved: false,
      pan,
    });
    this.setAim(event.clientX, event.clientY);
    if (this.pointers.size > 1) {
      this.gesture = true;
      this.onAction('useEnd');
    } else if (event.button === 0 && !pan) this.onAction('useStart');
  }

  private move(event: PointerEvent): void {
    if (!this.enabled) return;
    const pointer = this.pointers.get(event.pointerId);
    if (!pointer) {
      if (event.pointerType !== 'touch') this.setAim(event.clientX, event.clientY);
      return;
    }
    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;
    if (!pointer.moved && Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) > 6) {
      pointer.moved = true;
      this.onAction('useEnd');
    }
    if (pointer.touch && this.pointers.size >= 2) {
      const other = [...this.pointers.entries()].find(([id]) => id !== event.pointerId)![1];
      const before = Math.hypot(pointer.x - other.x, pointer.y - other.y);
      const after = Math.hypot(event.clientX - other.x, event.clientY - other.y);
      if (before > 5 && after > 5) this.zoom += Math.log(before / after);
      this.pan.x += dx * 0.5;
      this.pan.y += dy * 0.5;
      this.setAim((event.clientX + other.x) * 0.5, (event.clientY + other.y) * 0.5);
    } else if (pointer.pan || (pointer.touch && this.touchPan)) {
      this.pan.x += dx;
      this.pan.y += dy;
    } else if (pointer.touch || pointer.button === 2) {
      this.orbit.x += dx;
      this.orbit.y += dy;
    }
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    if (!pointer.touch) this.setAim(event.clientX, event.clientY);
  }

  private up(event: PointerEvent): void {
    const pointer = this.pointers.get(event.pointerId);
    if (!pointer) return;
    if (!pointer.moved && !pointer.pan && !this.gesture && pointer.button === 0) {
      this.setAim(event.clientX, event.clientY);
      this.onAction('use');
    }
    this.onAction('useEnd');
    this.pointers.delete(event.pointerId);
    if (!this.pointers.size) this.gesture = false;
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
  }

  private setAim(x: number, y: number): void {
    const rect = this.canvas.getBoundingClientRect();
    this.aim.set(((x - rect.left) / rect.width) * 2 - 1, 1 - ((y - rect.top) / rect.height) * 2);
  }

  private key(event: KeyboardEvent, down: boolean): void {
    if (!down) this.keys.delete(event.code);
    // A keyboard event from the interface belongs to its focused control.
    if ((event.target as HTMLElement).closest('#hud,input,select,textarea,[contenteditable="true"]')) return;
    const handled = [
      'KeyW',
      'KeyA',
      'KeyS',
      'KeyD',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Digit1',
      'Digit2',
      'Digit3',
      'KeyX',
      'KeyH',
      'KeyB',
      'KeyP',
      'Escape',
      'F3',
      'Backspace',
    ];
    if (handled.includes(event.code)) event.preventDefault();
    if (!down || (!this.enabled && !['Escape', 'KeyP', 'F3'].includes(event.code))) return;
    this.onGesture();
    this.keys.add(event.code);
    if (event.repeat) return;
    if (event.code === 'Digit1') this.onTool('c4');
    if (event.code === 'Digit2') this.onTool('plane');
    if (event.code === 'Digit3') this.onTool('airstrike');
    const actions: Record<string, Action> = {
      KeyX: 'detonate',
      KeyH: 'overview',
      KeyB: 'rebuild',
      Backspace: 'instant',
      KeyP: 'pause',
      Escape: 'pause',
      F3: 'debug',
    };
    if (actions[event.code]) this.onAction(actions[event.code]);
  }

  moveVector(): THREE.Vector2 {
    return new THREE.Vector2(
      Number(this.keys.has('KeyD') || this.keys.has('ArrowRight')) -
        Number(this.keys.has('KeyA') || this.keys.has('ArrowLeft')),
      Number(this.keys.has('KeyW') || this.keys.has('ArrowUp')) -
        Number(this.keys.has('KeyS') || this.keys.has('ArrowDown')),
    ).clampLength(0, 1);
  }

  cancel(reason?: 'interface'): void {
    for (const release of this.cancelListeners) release(reason);
    this.keys.clear();
    const captures = [...this.pointers.keys()];
    this.pointers.clear();
    for (const id of captures) if (this.canvas.hasPointerCapture(id)) this.canvas.releasePointerCapture(id);
    this.gesture = false;
    this.consume();
    this.onAction('useEnd');
  }

  consume(): void {
    this.orbit.set(0, 0);
    this.pan.set(0, 0);
    this.zoom = 0;
  }
}
